import { useCallback, useContext, useMemo, useState } from 'react';
import { AppContext } from '@rabbit/app-context';
import { DR_Consumer_Private } from '@rabbit/data/portal';
import { hasValidAddress, hasValidPhoneNumber } from '../utils/addressValidation';

export type ContactDetailsCheckResult =
  | { ok: true }
  | {
      ok: false;
      missingAddress: boolean;
      missingPhoneNumber: boolean;
    };

export function useContactDetailsCheck(params: {
  consumerPersonaId: string | null | undefined;
}) {
  const { consumerPersonaId } = params;
  const { config } = useContext(AppContext);

  const [showModal, setShowModal] = useState(false);
  const [missingAddress, setMissingAddress] = useState(false);
  const [missingPhoneNumber, setMissingPhoneNumber] = useState(false);

  const requirements = useMemo(() => {
    return {
      requireAddress: !!config?.CUSTOMER?.CLAIM_ADDRESS_MANDATORY,
      requirePhoneNumber: !!config?.CUSTOMER?.CLAIM_PHONE_MANDATORY,
    };
  }, [config?.CUSTOMER?.CLAIM_ADDRESS_MANDATORY, config?.CUSTOMER?.CLAIM_PHONE_MANDATORY]);

  const closeModal = useCallback(() => setShowModal(false), []);

  const ensureContactDetails = useCallback(async (): Promise<ContactDetailsCheckResult> => {
    const { requireAddress, requirePhoneNumber } = requirements;
    if (!requireAddress && !requirePhoneNumber) return { ok: true };

    const consumerData = await DR_Consumer_Private.get(consumerPersonaId ?? '');
    const hasAddress = requireAddress ? hasValidAddress(consumerData) : true;
    const hasPhone = requirePhoneNumber ? hasValidPhoneNumber(consumerData) : true;

    if (hasAddress && hasPhone) return { ok: true };

    const result: ContactDetailsCheckResult = {
      ok: false,
      missingAddress: !hasAddress,
      missingPhoneNumber: !hasPhone,
    };

    setMissingAddress(result.missingAddress);
    setMissingPhoneNumber(result.missingPhoneNumber);
    setShowModal(true);
    return result;
  }, [consumerPersonaId, requirements]);

  return {
    // state
    showModal,
    missingAddress,
    missingPhoneNumber,
    // actions
    closeModal,
    ensureContactDetails,
  };
}

